/**
 * @file: useAiWorkshopTools.ts
 * @copyright: (c) 2019-2020 sichuan zhichetech co., ltd.
 */

import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { useCurrentUser } from '@euler/app/flows/auth/context';
import { OrgUserRoleHelper } from '@euler/functions/OrgUserRoleHelper';
import { earliestTrialEnd } from '@euler/functions/entitlements/earliestTrialEnd';
import { useStoreFeatureLookup } from '@euler/functions/entitlements/useStoreFeature';
import { useStoreConfiguration } from '@euler/functions/useStoreConfiguration';
import { useCurrentUserRole } from '@euler/functions/user/useCurrentUserRole';
import { StoreFeature } from '@euler/model/enum';
import {
  CalendarDays,
  CircleCheck,
  ClipboardPlus,
  Clock,
  LucideIcon,
} from 'lucide-react-native';
import { useMemo } from 'react';

export type AiWorkshopToolKind = 'order' | 'ongoing' | 'finished' | 'plans';

export interface AiWorkshopTool {
  kind: AiWorkshopToolKind;
  title: string;
  icon: LucideIcon;
  color: string;
}

export interface AiWorkshopTools {
  tools: AiWorkshopTool[];
  /** The earliest end among the trials that give them; null when none. */
  trialEndsAt: string | null;
}

const kTools: (AiWorkshopTool & { feature: StoreFeature })[] = [
  {
    kind: 'order',
    title: '快速开单',
    icon: ClipboardPlus,
    color: aiColors.blue,
    feature: StoreFeature.OrderReception,
  },
  {
    kind: 'ongoing',
    title: '在厂车辆',
    icon: Clock,
    color: aiColors.green,
    feature: StoreFeature.OrderReception,
  },
  {
    kind: 'finished',
    title: '历史看板',
    icon: CircleCheck,
    color: aiColors.amber,
    feature: StoreFeature.OrderReception,
  },
  {
    kind: 'plans',
    title: '维保计划',
    icon: CalendarDays,
    color: aiColors.violet,
    feature: StoreFeature.MaintenancePlan,
  },
];

/**
 * The workshop tools an AI-edition store has, on trial or switched on for
 * it, for the AI home; null for any other store (the workshop app has its
 * own tabs for them). The finished orders follow the store's restriction
 * on who sees service records, as their tab does.
 */
export function useAiWorkshopTools(): AiWorkshopTools | null {
  const aiEdition = useCurrentUser()?.store.editionProductLine === 'ai';
  const featureOf = useStoreFeatureLookup();
  const storeConfiguration = useStoreConfiguration();
  const role = useCurrentUserRole();
  const restricted = storeConfiguration.restrictedRolesForServiceRecords;
  const canSeeRecords =
    !restricted?.length || new OrgUserRoleHelper(role).matchesAny(restricted);
  return useMemo(() => {
    if (!aiEdition) return null;
    const held = kTools.filter(
      x =>
        featureOf(x.feature).enabled &&
        (x.kind !== 'finished' || canSeeRecords),
    );
    if (held.length === 0) return null;
    return {
      tools: held.map(({ kind, title, icon, color }) => ({
        kind,
        title,
        icon,
        color,
      })),
      trialEndsAt: earliestTrialEnd(
        held.map(x => featureOf(x.feature).trialEndsAt),
      ),
    };
  }, [aiEdition, featureOf, canSeeRecords]);
}
